import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core";
import * as i18n from "../common/i18n";
import MaterialTable from 'material-table'
import { reactLocalStorage } from 'reactjs-localstorage';
import Chip from '@material-ui/core/Chip';
import LinearProgress from '@material-ui/core/LinearProgress';
import { CsvBuilder } from 'filefy';
import gql from 'graphql-tag';
import Message from "./Message";
import * as graphql from '../common/graphql';
import * as helper from "../common/helper";
import moment from "moment";
import ReloadIcon from '@material-ui/icons/Replay';
import QueuePlayNextIcon from '@material-ui/icons/QueuePlayNext';
import LeaseWizard from "./LeaseWizard";
import ExchangeDevice from "./ExchangeDevice";
import RouterIcon from '@material-ui/icons/Router';
import ChipLink from '../components/ChipLink';

import { withIAM, isInRoles, ROLE_ADMIN, ROLE_READER, ROLE_OWNER, ROLE_ASSISTANT, ROLE_EDITOR } from "../common/iam";

const styles = theme => ({
});


const defaults = {
    device: false,
    tenant: false,
    status: false,
    notes: false,
    tap: false,
    parcel: false,
    building: false,
    starts: false,
    ends: false,
    id: true,
    pageSize: 20,
};

const LeaseStatus = {
    "RUNNING": i18n.get("leases.status.RUNNING"),
    "PENDING": i18n.get("leases.status.PENDING"),
    "DONE": i18n.get("leases.status.DONE"),
}

const defaultsPropertyName = 'p71.leases.defaults';

class Leases extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            wizard: false,
            exchangeDevice: false,
            leaseData: null,
            working: false,
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
            userSettings: reactLocalStorage.getObject(defaultsPropertyName, defaults),
        };

        this.tableRef = React.createRef();
    }

    default(id) {
        return this.state.userSettings[id] !== undefined ? this.state.userSettings[id] : defaults[id];
    }

    setDefault(id, value) {

        const newUserSettings = { ...this.state.userSettings };
        newUserSettings[id] = value;

        this.setState({ userSettings: newUserSettings }, () => {
            reactLocalStorage.setObject(defaultsPropertyName, newUserSettings);
        });
    }

    getInput = (data) => {

        try {

            const input = {}
            const starts = moment(data.starts).unix()
            const ends = moment(data.ends).unix()
            data["starts"] = starts
            data["ends"] = ends

            helper.set(input, data, "device");
            helper.set(input, data, "tenant");
            helper.set(input, data, "building");
            helper.set(input, data, "parcel");
            helper.set(input, data, "tap");
            helper.set(input, data, "starts");
            helper.set(input, data, "ends");
            helper.set(input, data, "status");
            helper.set(input, data, "notes");
            helper.set(input, data, "address");

            return input;

        } catch (error) {
            throw new Error(i18n.get("leases.error.validation"))
        }
    }

    getUpdatedInput = (data) => {

        try {
            const input = {}

            helper.set(input, data, "device", d => d.uuid);
            helper.set(input, data, "status");
            helper.set(input, data, "notes");
            helper.set(input, data, "starts", mom => moment(mom).unix());
            helper.set(input, data, "ends", mom => moment(mom).unix());

            return input;

        } catch (error) {
            throw new Error(i18n.get("leases.error.validation"))
        }
    }

    getTenantSuggestions = async (value) => {
        try {
            const where = `[{column: "company", comparator: LIKE, string: "${value}"}]`;

            const result = await graphql.clientWithToken(this.props.accessToken).query({
                query: gql`
                query {
                    tenants(first: 5, offset: 0, where:${where} orderBy: [{column: "id", direction: ASC}]) {
                        totalCount
                        elements {
                            ... on Tenant {
                                id
                                company

                           }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            });

            return result.data.tenants.elements.map((element) => {
                return {
                    name: helper.tenantChipOf(element.company, element.id),
                    value: element.id,
                };
            });
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };


    getBuildingSuggestions = async (value) => {
        try {
            const where = `[{column: "street", comparator: LIKE, string: "${value}"}]`;

            const result = await graphql.clientWithToken(this.props.accessToken).query({
                query: gql`
                query {
                    buildings(first: 5, offset: 0, where:${where} orderBy: [{column: "id", direction: ASC}]) {
                        totalCount
                        elements {
                            ... on Building {
                                street
                                id
                           }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            });

            return result.data.buildings.elements.map((element) => {
                return {
                    name: helper.buildingChipOf(element.street, element.id),
                    value: element.id,
                };
            });
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    getParcelSuggestions = async (value) => {
        try {
            const where = `[{column: "name", comparator: LIKE, string: "${value}"}]`;

            const result = await graphql.clientWithToken(this.props.accessToken).query({
                query: gql`
                query {
                    parcels(first: 5, offset: 0, where:${where} orderBy: [{column: "id", direction: ASC}]) {
                        totalCount
                        elements {
                            ... on Parcel {
                                name
                                id
                                building {
                                    street
                                }
                           }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            });

            return result.data.parcels.elements.map((element) => {
                return {
                    name: helper.parcelChipOf(element.name, element.building.street),
                    value: element.id,
                };
            });
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    getTapSuggestions = async (value) => {
        try {
            const where = `[{column: "name", comparator: LIKE, string: "${value}"}]`;

            const result = await graphql.clientWithToken(this.props.accessToken).query({
                query: gql`
                query {
                    taps(first: 5, offset: 0, where:${where} orderBy: [{column: "id", direction: ASC}]) {
                        totalCount
                        elements {
                            ... on Tap {
                                name
                                valve
                                id
                           }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            });

            return result.data.taps.elements.map((element) => {
                return {
                    name: helper.tapChipOf(element.name, element.id, element.valve),
                    value: element.id,
                };
            });
        } catch (error) {
            console.log(error.message);
            return [];
        }
    };

    getDefaultSuggestions = () => {
        return [];
    };

    showMessage = (message, variant, details, hideAfter) => {
        this.setState({
            message: message,
            variant: variant ? variant : "info",
            hideAfter: hideAfter ? hideAfter : 3000,
            details: details ? details : null,
        })
    }

    closeMessage = () => {
        this.setState({
            message: null,
            details: null,
            hideAfter: 3000,
            variant: "info",
        })
    }

    mapField(column) {
        return column;
    }

    handleWizardCancel = () => {
        this.setState({ wizard: false, working: false })
    }

    handleWizardSave = (newData) => {
        this.setState({ wizard: false, working: false });
        new Promise(async (resolve, reject) => {
            try {

                const input = this.getInput(newData)
                const result = await graphql.clientWithToken(this.props.accessToken).mutate({
                    variables: {
                        "input": input,
                        "device": newData["deviceDetails"]["id"]
                    },
                    mutation: gql`
                    mutation addLease($input: LeaseInput!, $device: ID!) {
                        addLease(input: $input) {
                            id
                        }

                        updateDevice(id: $device, input: {status: LEASED}) {
                            id
                        }

                    }
                `,
                });

                this.showMessage(i18n.get("leases.message.onAdd.success"), "success")
                this.tableRef.current.onQueryChange();
                resolve();
            } catch (error) {
                this.showMessage(i18n.get("leases.message.onAdd.error"), "error", error.message);
                reject(error);
            }
        })
    }

    handleExchangeDeviceCancel = () => {
        this.setState({ exchangeDevice: false, working: false })
    }

    handleExchangeDeviceSave = (newData) => {
        this.setState({ exchangeDevice: false, working: false });
        new Promise(async (resolve, reject) => {
            try {

                const leaseID = newData["leaseData"]["id"];

                const newDevice = newData["deviceDetails"]["id"];

                const oldDevice = newData["leaseData"]["device"]["id"];

                const oldDeviceStatus = newData["oldDeviceStatus"];

                const updateInput = {
                    ends: moment(newData["exchangeDate"]).unix(),
                    status: "DONE",
                    notes: `Spüler am ${moment(newData["exchangeDate"]).format('DD.MM.YYYY')} gegen Gerät ${newData["device"]} ausgetauscht`
                };

                const addInput = {
                    device: newData["device"],
                    tenant: newData["leaseData"]["tenant"]["id"],
                    building: newData["leaseData"]["building"]["id"],
                    parcel: newData["leaseData"]["parcel"]["id"],
                    tap: newData["leaseData"]["tap"]["id"],
                    address: `${newData["leaseData"]["building"]["street"]}, ${newData["leaseData"]["parcel"]["name"]}, ${newData["leaseData"]["building"]["zipCode"]}, ${newData["leaseData"]["building"]["place"]}`,
                    status: "RUNNING",
                    starts: moment(newData["exchangeDate"]).unix(),
                    ends: moment(newData["leaseData"]["ends"]).unix(),
                    notes: newData["leaseData"]["notes"]
                };


                const result = await graphql.clientWithToken(this.props.accessToken).mutate({
                    variables: {
                        "id": leaseID,
                        "updateInput": updateInput,
                        "addInput": addInput,
                        "newDevice": newDevice,
                        "oldDevice": oldDevice,
                        "oldDeviceStatus": oldDeviceStatus
                    },
                    mutation: gql`
                    mutation exchangeLease($id: ID!, $addInput: LeaseInput!, $updateInput: LeaseInput!, $newDevice: ID!, $oldDevice: ID!, $oldDeviceStatus: DeviceStatus!) {
                        
                        addLease(input: $addInput) {
                            id
                        }
                        
                        updateLease(id: $id, input: $updateInput) {
                            id
                        }

                        updateNewDevice: updateDevice(id: $newDevice, input: {status: LEASED}) {
                            id
                        }

                        updateOldDevice: updateDevice(id: $oldDevice, input: {status: $oldDeviceStatus}) {
                            id
                        }
                    
                    }
                `,
                });

                this.showMessage(i18n.get("leases.message.onExchangeDevice.success"), "success")
                this.tableRef.current.onQueryChange();
                resolve();
            } catch (error) {
                this.showMessage(i18n.get("leases.message.onExchangeDevice.error"), "error", error.message);
                reject(error);
            }
        })
    }

    exportCsv = async (columns, data) => {

        try {

            this.setState({
                exporting: true,
                progressValue: 0
            });

            const allLeases = [];
            let totalCount = 0;
            const first = 100;
            let offset = 0

            this.showMessage("Export wird angestoßen. Bitte verlassen Sie die Seite nicht, solange der Exportvorgang läuft.", "info");

            do {
                
                const filters = []
                for (const column of columns) {
                    if (column.tableData.filterValue !== undefined && column.tableData.filterValue !== null) {
                        filters.push({
                            column: column,
                            field: column.field,
                            type: column.type,
                            comparator: column.comparator,
                            value: column.tableData.filterValue
                        })
                    }
                }

                const find = helper.whereString(filters, this.mapField, ["id", "device", "status"]);

                const result = await graphql.clientWithToken(this.props.accessToken).query({
                    query: gql`
                    query {
                        leases(first: 100, offset: ${offset}, where: ${find}, orderBy: [{column: "id" direction: ASC}]) {
                            totalCount
                            elements {
                                ... on Lease {
                                    id
                                    notes
                                    device {
                                        uuid
                                    },
                                    tenant {
                                        id
                                        company
                                    },
                                    status,
                                    tap {
                                        name
                                    },
                                    parcel {
                                        name
                                        building {
                                            street
                                        }
                                    },
                                    starts {
                                        isoString
                                    },
                                    ends {
                                        isoString
                                    },

                                }
                            }
                        }
                    }
                `,
                    fetchPolicy: "no-cache"
                });

                totalCount = result.data.leases.totalCount;
                offset = offset + first;
                for (const lease of result.data.leases.elements) {
                    allLeases.push(
                        {
                            id: lease.id,
                            device: lease.device ? lease.device.uuid : '-',
                            tenant: lease.tenant ? lease.tenant.company : '-',
                            building: lease.parcel && lease.parcel.building ? lease.parcel.building.street : '-',
                            status: i18n.get(`leases.status.${lease.status}`),
                            notes: lease.notes,
                            tap: lease.tap ? lease.tap.name : '-',
                            parcel: lease.parcel ? lease.parcel.name : '-',
                            starts: lease.starts.isoString ? moment(lease.starts.isoString).format('DD.MM.YYYY') : '-',
                            ends: lease.ends.isoString ? moment(lease.ends.isoString).format('DD.MM.YYYY') : '-',
                        }
                    )
                }

                if (totalCount !== 0) {
                    this.setState({
                        exporting: true,
                        progressValue: (allLeases.length / totalCount) * 100
                    });
                }
            } while (allLeases.length < totalCount);

            const builder = new CsvBuilder('Mieten.csv')
                .setDelimeter(";")
                .setColumns(columns.map(columnDef => columnDef.title))
                .addRows(allLeases.map(lease => { return [lease.device, lease.tenant, lease.building, lease.parcel, lease.tap, lease.status, lease.notes, lease.starts, lease.ends, lease.id] }))
                .exportFile();

            this.setState({
                exporting: false,
                progressValue: 0
            });
        } catch (error) {
            this.setState({
                exporting: false,
                progressValue: 0
            });
            this.showMessage(error.message, "error");
        }

    }

    render() {
        return (
            <div style={{ maxWidth: '100%' }}>

                {this.state.exporting && <div style={{ width: "100%" }}><LinearProgress value={this.state.progressValue} variant={"determinate"} /></div>}

                <MaterialTable
                    components={{
                        Cell: helper.defaultCell,
                        FilterRow: helper.defaultFilter
                    }}
                    icons={helper.tableIcons}
                    onChangeRowsPerPage={pageSize => { this.setDefault("pageSize", pageSize) }}
                    onChangeColumnHidden={data => { this.setDefault(data["field"], data["hidden"]) }}
                    tableRef={this.tableRef}
                    localization={i18n.getLocalization()}
                    columns={[
                        {
                            title: i18n.get("leases.device"),
                            field: 'device',
                            hidden: this.default("device"),
                            sorting: true,
                            filtering: true,
                            editable: "onAdd",
                            cellStyle: {
                                minWidth: '100px',
                                maxWidth: '100px',
                            },
                            headerStyle: {
                                minWidth: '100px',
                                maxWidth: '100px',
                            },
                            render: (rowData) => {
                                if (rowData) {
                                    return <ChipLink
                                        icon={<RouterIcon />}
                                        link={{ page: i18n.get('menus.devices'), data: { uuid: rowData.device.uuid } }}
                                        key={rowData.device.uuid}
                                        label={`${rowData.device.uuid}`}
                                        style={{ marginRight: 8, marginBottom: 8 }}
                                    />
                                }
                            },
                        },
                        {
                            title: i18n.get("leases.tenant"),
                            field: 'tenant',
                            hidden: this.default("tenant"),
                            sorting: true,
                            filtering: true,
                            editable: "never",
                            comparator: 'IS',
                            cellStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            headerStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            filterComponent: helper.chipsFilter(
                                (items, props) =>
                                    props.onFilterChanged(
                                        props.columnDef.tableData.id,
                                        items.map((item) => `${item.value}`)
                                    ),
                                (value) =>
                                    value.map((v) => {
                                        return {
                                            name: `${v.company}`,
                                            value: v.id,
                                        };
                                    }),
                                this.getTenantSuggestions,
                                this.getDefaultSuggestions
                            ),
                            render: (rowData) => {
                                if (rowData) {
                                    return <ChipLink
                                        link={{ page: i18n.get('menus.tenants'), data: { id: rowData.tenant.id } }}
                                        key={rowData.tenant.id}
                                        label={`${rowData.tenant.company}`} value={rowData.tenant.id}
                                        style={{ marginRight: 8, marginBottom: 8 }}
                                    />
                                }
                            },
                        },
                        {
                            title: i18n.get("leases.building"),
                            field: 'building',
                            hidden: this.default("building"),
                            cellStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            headerStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            sorting: true,
                            filtering: true,
                            editable: "never",
                            comparator: 'IS',
                            filterComponent: helper.chipsFilter(
                                (items, props) =>
                                    props.onFilterChanged(
                                        props.columnDef.tableData.id,
                                        items.map((item) => `${item.value}`)
                                    ),
                                (value) =>
                                    value.map((v) => {
                                        return {
                                            name: `${v.label}`,
                                            value: v.id,
                                        };
                                    }),
                                this.getBuildingSuggestions,
                                this.getDefaultSuggestions
                            ),
                            // render: rowData => { return rowData.building && rowData.building.street ? <Chip label={`${rowData.building.street}`} value={rowData.building.id} /> : null },
                            render: (rowData) => {
                                if (rowData) {
                                    return <ChipLink
                                        link={{ page: i18n.get('menus.buildings'), data: { id: rowData.building.id } }}
                                        key={rowData.building.id}
                                        label={`${rowData.building.street}`}
                                        style={{ marginRight: 8, marginBottom: 8 }}
                                    />
                                }
                            },
                        },
                        {
                            title: i18n.get("leases.parcel"),
                            field: 'parcel',
                            cellStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            headerStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            hidden: this.default("parcel"),
                            sorting: true,
                            filtering: true,
                            editable: "never",
                            comparator: 'IS',
                            filterComponent: helper.chipsFilter(
                                (items, props) =>
                                    props.onFilterChanged(
                                        props.columnDef.tableData.id,
                                        items.map((item) => `${item.value}`)
                                    ),
                                (value) =>
                                    value.map((v) => {
                                        return {
                                            name: `${v.label}`,
                                            value: v.id,
                                        };
                                    }),
                                this.getParcelSuggestions,
                                this.getDefaultSuggestions
                            ),
                            // render: rowData => { return rowData.parcel && rowData.parcel.name ? <Chip label={`${rowData.parcel.name} (${rowData.building.street})`} value={rowData.parcel.id} /> : null },
                            render: (rowData) => {
                                if (rowData) {
                                    return <ChipLink
                                        link={{ page: i18n.get('menus.parcels'), data: { id: rowData.parcel.id } }}
                                        key={rowData.parcel.id}
                                        label={`${rowData.parcel.name} (${rowData.building.street})`}
                                        style={{ marginRight: 8, marginBottom: 8 }}
                                    />
                                }
                            },
                        },
                        {
                            title: i18n.get("leases.tap"),
                            field: 'tap',
                            cellStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            headerStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            hidden: this.default("tap"),
                            sorting: true,
                            filtering: true,
                            editable: "never",
                            comparator: 'IS',
                            filterComponent: helper.chipsFilter(
                                (items, props) =>
                                    props.onFilterChanged(
                                        props.columnDef.tableData.id,
                                        items.map((item) => `${item.value}`)
                                    ),
                                (value) =>
                                    value.map((v) => {
                                        return {
                                            name: `${v.label}`,
                                            value: v.id,
                                        };
                                    }),
                                this.getTapSuggestions,
                                this.getDefaultSuggestions
                            ),
                            // render: rowData => { return rowData.tap && rowData.tap.name && rowData.tap.valve ? <Chip label={`${rowData.tap.name}/${rowData.tap.valve} (${rowData.parcel.name})`} value={rowData.tap.id} /> : null },
                            render: (rowData) => {
                                if (rowData) {
                                    return <ChipLink
                                        link={{ page: i18n.get('menus.taps'), data: { id: rowData.tap.id } }}
                                        key={rowData.tap.id}
                                        label={`${rowData.tap.name}/${rowData.tap.valve} (${rowData.parcel.name})`}
                                        style={{ marginRight: 8, marginBottom: 8 }}
                                    />
                                }
                            },
                        },
                        {
                            title: i18n.get("leases.status"),
                            field: 'status',
                            hidden: this.default("status"),
                            cellStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            headerStyle: {
                                minWidth: '300px',
                                maxWidth: '300px',
                            },
                            lookup: LeaseStatus,
                            sorting: true,
                            filtering: true,
                            editable: "always",
                        },
                        {
                            title: i18n.get("leases.notes"),
                            field: 'notes',
                            hidden: this.default("notes"),
                            cellStyle: {
                                minWidth: '500px',
                                maxWidth: '500px',
                            },
                            headerStyle: {
                                minWidth: '500px',
                                maxWidth: '500px',
                            },
                            sorting: true,
                            filtering: true,
                            editable: "always",
                        },
                        {
                            title: i18n.get("leases.starts"),
                            field: 'starts',
                            hidden: this.default("starts"),
                            type: "date",
                            cellStyle: {
                                width: '400px',
                                maxWidth: '400px',
                            },
                            headerStyle: {
                                width: '400px',
                                maxWidth: '400px',
                            },
                            filterComponent: helper.DateRangeFilter,
                            sorting: true,
                            filtering: true,
                            editable: "always",
                        },
                        {
                            title: i18n.get("leases.ends"),
                            field: 'ends',
                            hidden: this.default("ends"),
                            type: "date",
                            cellStyle: {
                                minWidth: '400px',
                                maxWidth: '400px',
                            },
                            headerStyle: {
                                minWidth: '400px',
                                maxWidth: '400px',
                            },
                            filterComponent: helper.DateRangeFilter,
                            sorting: true,
                            filtering: true,
                            editable: "always",
                        },
                        {
                            title: i18n.get("leases.id"),
                            field: 'id',
                            hidden: this.default("id"),
                            cellStyle: {
                                minWidth: '100px',
                                maxWidth: '100px',
                            },
                            headerStyle: {
                                minWidth: '100px',
                                maxWidth: '100px',
                            },
                            sorting: true,
                            customSort: (a, b) => parseInt(a.id) - parseInt(b.id),
                            filtering: true,
                            editable: "never",
                        },
                    ]}
                    actions={isInRoles(this.props.roles, [ROLE_ADMIN, ROLE_OWNER, ROLE_EDITOR]) ? [
                        {
                            isFreeAction: true,
                            icon: QueuePlayNextIcon,
                            tooltip: i18n.get("leases.addLease.tooltip"),
                            onClick: async (event, rowData) => {

                                this.setState({
                                    wizard: true,
                                })
                            }
                        },
                        {
                            isFreeAction: true,
                            icon: () => { return <ReloadIcon /> },
                            tooltip: i18n.get("general.reload"),
                            onClick: async (event, rowData) => {
                                this.tableRef.current.onQueryChange();
                            }
                        },
                        {
                            isFreeAction: false,
                            icon: () => { return <RouterIcon /> },
                            tooltip: i18n.get("leases.exchangeDevice"),
                            onClick: (event, rowData) => {
                                this.setState({
                                    exchangeDevice: true,
                                    leaseData: rowData
                                })
                            }
                        }
                    ] : [
                        {
                            isFreeAction: true,
                            icon: () => { return <ReloadIcon /> },
                            tooltip: i18n.get("general.reload"),
                            onClick: async (event, rowData) => {
                                this.tableRef.current.onQueryChange();
                            }
                        },
                    ]}

                    data={query =>
                        new Promise(async (resolve, reject) => {

                            try {


                                const where = helper.whereString(query['filters'], this.mapField, ["id", "device", "status"]);
                                let orderBy = helper.orderByString(query, this.mapField, 'id', true);
                                if (where === "[]") {
                                    orderBy = helper.orderByString(query, this.mapField, "device")
                                }

                                const result = await graphql.clientWithToken(this.props.accessToken).query({
                                    query: gql`
                                    query {
                                        leases(first: ${query.pageSize}, offset: ${query.pageSize * query.page}, orderBy:${orderBy}, where:${where}) {
                                            totalCount
                                            elements {
                                                ... on Lease {
                                                    id
                                                    notes
                                                    device {
                                                        uuid
                                                        id
                                                    },
                                                    tenant {
                                                        id
                                                        company
                                                    },
                                                    status,
                                                    tap {
                                                        id
                                                        name
                                                        valve
                                                    },
                                                    parcel {
                                                        id
                                                        name

                                                    },
                                                    building {
                                                            id
                                                            street
                                                            zipCode
                                                            place
                                                        }
                                                    starts {
                                                        isoString
                                                    },
                                                    ends {
                                                        isoString
                                                    },

                                               }
                                            }
                                        }
                                    }
                                `,
                                    fetchPolicy: "no-cache"
                                });

                                const searchResult = {
                                    page: query.page,
                                    data: result.data.leases.elements.map(
                                        (element) => {
                                            return {
                                                id: element.id,
                                                device: element.device,
                                                tenant: element.tenant,
                                                building: element.building,
                                                status: element.status,
                                                notes: element.notes,
                                                tap: element.tap,
                                                parcel: element.parcel,
                                                starts: element.starts.isoString ? moment(element.starts.isoString).toDate() : null,
                                                ends: element.ends.isoString ? moment(element.ends.isoString).toDate() : null,
                                            };
                                        }),
                                    totalCount: result.data.leases.totalCount
                                }
                                resolve(searchResult);
                            } catch (error) {
                                this.showMessage(error.message, "error");
                                reject(error);
                            }
                        })
                    }
                    title={i18n.get("leases.title")}
                    options={{
                        addRowPosition: "first",
                        exportButton: !this.state.exporting,
                        filtering: true,
                        showTitle: false,
                        debounceInterval: 500,
                        search: false,
                        columnsButton: true,
                        exportCsv: this.exportCsv,
                        pageSize: this.default("pageSize"),
                        pageSizeOptions: [5, 20, 50]
                    }}

                    editable={{
                        isEditable: rowData => isInRoles(this.props.roles, [ROLE_ADMIN, ROLE_OWNER, ROLE_EDITOR]),
                        isDeletable: rowData => isInRoles(this.props.roles, [ROLE_ADMIN, ROLE_OWNER, ROLE_EDITOR]),
                        onRowUpdate: (newData, oldData) =>
                            new Promise(async (resolve, reject) => {

                                try {

                                    const input = this.getUpdatedInput(newData);

                                    const result = await graphql.clientWithToken(this.props.accessToken).mutate({
                                        variables: {
                                            "id": newData.id,
                                            "input": input,
                                        },
                                        mutation: gql`
                                        mutation updateLease($id: ID!, $input: LeaseInput!) {
                                            updateLease(id: $id, input: $input) {
                                                id
                                            }
                                        }
                                    `,
                                    });


                                    this.showMessage(i18n.get("leases.message.onRowUpdate.success"), "success")

                                    resolve(result);
                                } catch (error) {

                                    this.showMessage(i18n.get("leases.message.onRowUpdate.error"), "error", error.message)
                                    reject(error);
                                }
                            }),
                        onRowDelete: oldData =>
                            new Promise(async (resolve, reject) => {

                                try {

                                    const result = await graphql.clientWithToken(this.props.accessToken).mutate({
                                        variables: {
                                            "id": oldData.id,
                                        },
                                        mutation: gql`
                                            mutation deleteLease($id: ID!) {
                                                deleteLease(id: $id)
                                            }
                                        `,
                                    });

                                    this.showMessage(i18n.get("leases.message.onRowDelete.success"), "success")

                                    resolve(result);
                                } catch (error) {
                                    this.showMessage(i18n.get("leases.message.onRowDelete.error"), "error", error.message)
                                    reject(error);
                                }
                            }),
                    }}
                />

                <Message
                    hideAfter={this.state.hideAfter}
                    onClose={this.closeMessage}
                    variant={this.state.variant}
                    message={this.state.message}
                    details={this.state.details}
                    open={this.state.message !== null}
                />

                <LeaseWizard
                    open={this.state.wizard}
                    working={this.state.working}
                    onCancel={this.handleWizardCancel}
                    accessToken={this.props.accessToken}
                    onSave={this.handleWizardSave} />

                <ExchangeDevice
                    open={this.state.exchangeDevice}
                    data={this.state.leaseData}
                    working={this.state.working}
                    onCancel={this.handleExchangeDeviceCancel}
                    accessToken={this.props.accessToken}
                    onSave={this.handleExchangeDeviceSave} />

            </div>
        )
    }
}

Leases.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withIAM(withStyles(styles, { withTheme: true })(Leases))